import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, List, Heart, RefreshCw } from 'lucide-react';
import Header from '@/components/layout/Header';
import ComboCard from '@/components/combo/ComboCard';
import ComboCreator from '@/components/combo/ComboCreator';
import ComboPlayer from '@/components/combo/ComboPlayer';
import { comboApi } from '@/lib/api';
import { AUDIO_COLORS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';

const CombosPage = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('feed'); // feed, my, saved
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreator, setShowCreator] = useState(false);
  const [playingCombo, setPlayingCombo] = useState(null);
  const [comboColorFilter, setComboColorFilter] = useState(null);

  const loadCombos = async () => {
    setLoading(true);
    try {
      let data;
      switch (tab) {
        case 'my':
          data = await comboApi.getMyCombos();
          break;
        case 'saved':
          data = await comboApi.getSavedCombos();
          break;
        default:
          data = await comboApi.getFeed(0, 20, comboColorFilter);
      }
      setCombos(data);
    } catch (error) {
      console.error('Error loading combos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      loadCombos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authLoading, comboColorFilter]);

  // Alarma: reproducir combo automáticamente si llega ?play=<comboId>
  useEffect(() => {
    const playId = searchParams.get('play');
    if (playId && !loading) {
      (async () => {
        try {
          const combo = await comboApi.getCombo(playId);
          setPlayingCombo(combo);
        } catch (error) {
          console.error('Error reproduciendo combo desde alarma:', error);
        } finally {
          setSearchParams({}, { replace: true });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const handleCreateSuccess = (newCombo) => {
    setShowCreator(false);
    setCombos(prev => [newCombo, ...prev]);
  };

  const handleDelete = (comboId) => {
    setCombos(prev => prev.filter(c => c.combo_id !== comboId));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="combos-page">
      <Header onRecordClick={() => navigate('/dashboard')} />

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">🎧 Combos</h1>
            <p className="text-sm text-white/60">Mini-playlists de hasta 5 minutos</p>
          </div>
          {isAuthenticated && (
            <button
              onClick={() => setShowCreator(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium shadow-lg"
              data-testid="create-combo-btn"
            >
              <Plus size={20} />
              <span>Crear</span>
            </button>
          )}
        </div>

        {/* Creator Modal */}
        {showCreator && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg">
              <ComboCreator
                onSuccess={handleCreateSuccess}
                onCancel={() => setShowCreator(false)}
              />
            </div>
          </div>
        )}

        {/* Player Modal */}
        {playingCombo && (
          <ComboPlayer
            combo={playingCombo}
            onClose={() => setPlayingCombo(null)}
            onUpdate={loadCombos}
          />
        )}

        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-2 mb-4 flex gap-2 border border-white/10">
          <button
            onClick={() => setTab('feed')}
            className={`flex-1 py-2 px-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
              tab === 'feed' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/10'
            }`}
            data-testid="tab-feed"
          >
            <List size={18} />
            <span className="text-sm font-medium">Explorar</span>
          </button>
          {isAuthenticated && (
            <>
              <button
                onClick={() => setTab('my')}
                className={`flex-1 py-2 px-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                  tab === 'my' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/10'
                }`}
                data-testid="tab-my"
              >
                <Plus size={18} />
                <span className="text-sm font-medium">Mis Combos</span>
              </button>
              <button
                onClick={() => setTab('saved')}
                className={`flex-1 py-2 px-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                  tab === 'saved' ? 'bg-purple-500 text-white' : 'text-white/70 hover:bg-white/10'
                }`}
                data-testid="tab-saved"
              >
                <Heart size={18} />
                <span className="text-sm font-medium">Guardados</span>
              </button>
            </>
          )}
        </div>

        {/* Filtro por temática (solo en Explorar) */}
        {tab === 'feed' && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
            <button
              onClick={() => setComboColorFilter(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                comboColorFilter === null
                  ? 'bg-purple-500 text-white border-purple-500'
                  : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
              }`}
              data-testid="combo-filter-all"
            >
              Todas
            </button>
            {Object.entries(AUDIO_COLORS).map(([colorKey, colorData]) => (
              <button
                key={colorKey}
                onClick={() => setComboColorFilter(comboColorFilter === colorKey ? null : colorKey)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  comboColorFilter === colorKey
                    ? 'bg-purple-500 text-white border-purple-500'
                    : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                }`}
                data-testid={`combo-filter-${colorKey}`}
              >
                {colorData.emoji} {colorData.name}
              </button>
            ))}
          </div>
        )}

        {/* Refresh */}
        <div className="flex justify-end mb-4">
          <button
            onClick={loadCombos}
            disabled={loading}
            className="bg-white/10 text-white/70 hover:bg-white/20 px-3 py-2 rounded-xl flex items-center gap-2 transition-colors"
            data-testid="refresh-combos"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="text-sm">Actualizar</span>
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full" />
          </div>
        ) : combos.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 text-center border border-white/10">
            <p className="text-4xl mb-4">📦</p>
            <p className="text-white/60">
              {tab === 'feed' && 'No hay combos todavía'}
              {tab === 'my' && 'No has creado combos'}
              {tab === 'saved' && 'No tienes combos guardados'}
            </p>
            {tab !== 'feed' && (
              <button
                onClick={() => setShowCreator(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl mt-4 font-medium"
              >
                Crear mi primer combo
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {combos.map((combo) => (
              <ComboCard
                key={combo.combo_id}
                combo={combo}
                onPlay={setPlayingCombo}
                onUpdate={loadCombos}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CombosPage;
